import type { ActivityTask, ActivityAction, ActivityState } from '~~/shared/activity/types'
import { isIndexStatePending as isPending } from '~~/shared/items/itemPending'
import { isOwnQuotaBlock, quotaBlockMessage } from '~/utils/quotaBlockMessage'
import type { IndexJob, IndexStage } from '~/composables/documents-indexing/indexJob'

/** The translator the projection is handed — the store's `useI18n().t`. */
type Translate = Parameters<typeof quotaBlockMessage>[0]

/** Phases are ordered, so reaching 'analyzing' means 'reading' is behind us. */
const PHASE_ORDER: IndexStage[] = ['reading', 'analyzing']

/**
 * The Activity Center projection of ONE indexing job (MAR-948: split out of the indexing store,
 * which still owns the poller, the state machine and the quota payload, and publishes this).
 *
 * Pure, with `t` handed in: all localization happens here, because `ActivityTask.detail` is
 * contractually already-localized — the panel renders indexing without knowing anything about it.
 */

/** Job state → the four coarse activity states. `attention` means a human has to decide. */
function activityState(job: IndexJob): ActivityState {
  // `uploading` is running, not queued: the bytes are moving, and calling it queued would read as
  // "waiting for a worker" when nothing is waiting.
  if (job.state === 'uploading') return 'running'
  if (isPending(job.state)) return job.state === 'queued' ? 'queued' : 'running'
  if (job.state === 'failed' || job.state === 'quota_blocked' || job.state === 'awaiting_confirmation') return 'attention'
  return 'done'
}

/** The one line under the title — the same text the docked tray used to render. */
function activityDetail(job: IndexJob, t: Translate, viewerId?: string | null): string {
  if (job.state === 'uploading') return t('indexing.uploading')
  if (job.state === 'working' && job.stale) return t('indexing.stalled')
  if (job.state === 'working' && job.stage) return t(`indexing.stage.${job.stage}`)
  if (job.state === 'quota_blocked') return quotaBlockMessage(t, job.quota, viewerId)
  if (job.state === 'failed') return job.errorMessage || t('indexing.failedGeneric')
  if (job.state === 'awaiting_confirmation') return t('indexing.awaitingConfirmation')
  if (job.state === 'queued') return t('indexing.queued')
  if (job.state === 'duplicate') return t('indexing.duplicate')
  if (job.state === 'ready') return t('indexing.ready')
  return t('indexing.working')
}

function activityBadge(job: IndexJob, t: Translate): ActivityTask['badge'] {
  // Amber, never red: a quota block means nothing broke and the document is intact (§5).
  if (job.state === 'quota_blocked') return { text: t('indexing.quotaBadge'), tone: 'warn' }
  if (job.state === 'awaiting_confirmation') return { text: t('indexing.confirmBadge'), tone: 'ask' }
  if (job.state === 'failed') return { text: t('indexing.failedBadge'), tone: 'bad' }
  if (job.state === 'duplicate') return { text: t('indexing.duplicateBadge'), tone: 'good' }
  return undefined
}

function activityActions(job: IndexJob, t: Translate, viewerId?: string | null): ActivityAction[] {
  // An acknowledged row offers exactly one thing: undo. Retry and Ver uso stay available on the
  // live row, and repeating them here would invite acting on work the user has already filed away.
  if (job.dismissedAt) return [{ id: 'restore', label: t('activity.restore'), kind: 'run' }]
  // The way out of an upload that hangs without ever resolving or erroring — and the only action
  // available while one is still in flight.
  if (job.state === 'uploading') return [{ id: 'cancel', label: t('indexing.cancel'), kind: 'run' }]

  const actions: ActivityAction[] = []
  // Retry re-runs the pipeline from the STORED file, so it needs a server row. An upload that was
  // refused never produced one — there is nothing to reprocess, and the user has to pick the file
  // again. NO retry on a quota block either: the pipeline would re-run, hit the same gate and fail
  // again, and offering the button implies the user did something wrong (§5).
  // A block ANOTHER user's seat hit is the exception (MAR-1046 AC17): the viewer's own allowance
  // was never asked, so a re-run is billed to their seat and may well succeed.
  const ownBlock = job.state === 'quota_blocked' && isOwnQuotaBlock(job.quota, viewerId)
  if (job.itemId && (job.state === 'failed' || (job.state === 'working' && job.stale) || (job.state === 'quota_blocked' && !ownBlock))) {
    actions.push({ id: 'retry', label: t('indexing.retry'), kind: 'run' })
  }
  if (ownBlock) {
    actions.push({ id: 'usage', label: t('indexing.viewUsage'), kind: 'link', to: '/app/settings/usage' })
  }
  if (!isPending(job.state)) actions.push({ id: 'dismiss', label: t('indexing.dismiss'), kind: 'run' })
  return actions
}

/** `viewerId` is the signed-in user's id: a quota block is explained differently to the user whose seat hit it. */
export function indexingTask(job: IndexJob, t: Translate, viewerId?: string | null): ActivityTask {
  return {
    // Namespaced so ids stay unique once other kinds publish here. Keyed on the JOB, not the item:
    // that is what keeps the row identical either side of the upload→indexing handoff.
    id: `indexing:${job.id}`,
    kind: 'indexing',
    title: job.name,
    detail: activityDetail(job, t, viewerId),
    state: activityState(job),
    phases: job.state === 'working' && job.stage && !job.stale
      ? PHASE_ORDER.map(p => ({ label: t(`indexing.stage.${p}`), done: PHASE_ORDER.indexOf(p) <= PHASE_ORDER.indexOf(job.stage!) }))
      : undefined,
    badge: activityBadge(job, t),
    actions: activityActions(job, t, viewerId),
    startedAt: job.startedAt,
    settledAt: job.settledAt,
    dismissed: !!job.dismissedAt,
  }
}
